import React, { useState, createContext, useContext, useEffect } from "react";

import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { createPortal } from "react-dom";

import {
  DEFAULT_TOAST_DURATION,
  normalizeToastContent,
  toastManager,
  toast,
  type ToastContent,
  type ToastType,
} from "../../Utils/Helper/toast";
import "./style.scss";

// Re-export toast utility for convenience (so users can import from either location)
export { toast };
export type { ToastContent, ToastType };

interface ToastItem {
  id: number;
  title: string;
  message: string;
  type: ToastType;
  duration: number;
}

const ToastContext = createContext<{
  success: (content: ToastContent, duration?: number) => number;
  error: (content: ToastContent, duration?: number) => number;
  warning: (content: ToastContent, duration?: number) => number;
  info: (content: ToastContent, duration?: number) => number;
  addToast: (
    content: ToastContent,
    type?: ToastType,
    duration?: number
  ) => number;
  removeToast: (id: number) => void;
} | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toastItem => toastItem.id !== id));
  };

  const addToast = (
    content: ToastContent,
    type: ToastType = "info",
    duration = DEFAULT_TOAST_DURATION
  ): number => {
    const id = Date.now() + Math.random();
    const { title, message } = normalizeToastContent(content, type);
    const toastItem: ToastItem = {
      id,
      title,
      message,
      type,
      duration,
    };

    setToasts(prev => [...prev, toastItem]);

    if (duration > 0) {
      window.setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  const success = (content: ToastContent, duration?: number) =>
    addToast(content, "success", duration);
  const error = (content: ToastContent, duration?: number) =>
    addToast(content, "error", duration);
  const warning = (content: ToastContent, duration?: number) =>
    addToast(content, "warning", duration);
  const info = (content: ToastContent, duration?: number) =>
    addToast(content, "info", duration);

  useEffect(() => {
    toastManager.setToastFunctions({ success, error, warning, info });
  }, []);

  return (
    <ToastContext.Provider
      value={{ success, error, warning, info, addToast, removeToast }}
    >
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({
  toasts,
  removeToast,
}: {
  toasts: ToastItem[];
  removeToast: (id: number) => void;
}) {
  if (toasts.length === 0) {
    return null;
  }

  const container = (
    <div className="toast-wrapper">
      {toasts.map(toastItem => (
        <Toast
          key={toastItem.id}
          toast={toastItem}
          onClose={() => removeToast(toastItem.id)}
        />
      ))}
    </div>
  );

  return createPortal(container, document.body);
}

function ToastIcon({ type }: { type: ToastType }) {
  switch (type) {
    case "success":
      return <CheckCircle2 aria-hidden="true" />;
    case "error":
      return <XCircle aria-hidden="true" />;
    case "warning":
      return <AlertTriangle aria-hidden="true" />;
    case "info":
    default:
      return <Info aria-hidden="true" />;
  }
}

function Toast({
  toast: toastItem,
  onClose,
}: {
  toast: ToastItem;
  onClose: () => void;
}) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    window.setTimeout(onClose, 300);
  };

  const toastClass = [
    "custom-toast",
    `custom-toast--${toastItem.type}`,
    isExiting ? "custom-toast--exiting" : "custom-toast--entering",
  ].join(" ");

  return (
    <div
      className={toastClass}
      style={
        {
          "--toast-duration": `${toastItem.duration}ms`,
        } as React.CSSProperties
      }
      role="alert"
      aria-live="polite"
    >
      <div className="toast-accent" aria-hidden="true" />

      <div className="toast-body">
        <div className="toast-icon-wrap">
          <ToastIcon type={toastItem.type} />
        </div>

        <div className="toast-content">
          <p className="toast-title">{toastItem.title}</p>
          <p className="toast-message">{toastItem.message}</p>
        </div>

        <button
          type="button"
          onClick={handleClose}
          className="toast-close"
          aria-label="Close notification"
        >
          <X aria-hidden="true" />
        </button>
      </div>

      {toastItem.duration > 0 && (
        <div className="toast-progress-track" aria-hidden="true">
          <div className="toast-progress-bar" />
        </div>
      )}
    </div>
  );
}
